import { useGetHolidaysQuery } from "@/services/availability";
import { DateTime } from "luxon";
import { useCallback, useMemo } from "react";

export type FrenchHoliday = { date: string; name: string };

const NONE: FrenchHoliday[] = [];

const toDateKey = (date: string | Date | DateTime): string | null => {
  let dt: DateTime;
  if (date instanceof DateTime) dt = date;
  else if (date instanceof Date) dt = DateTime.fromJSDate(date);
  else dt = DateTime.fromISO(date);
  return dt.isValid ? dt.toFormat("yyyy-MM-dd") : null;
};

/**
 * Fériés de métropole calculés par l'API (`GET /holidays`), plus par
 * calendrier.api.gouv.fr : l'écran affiche exactement les fériés que l'API
 * applique dans ses règles de disponibilité (spec 2026-10-08, § 6.3).
 */
export const useFrenchHolidays = (years: number[]) => {
  const { data } = useGetHolidaysQuery(years);
  const holidays = data ?? NONE;

  const holidayByDate = useMemo(() => {
    const map = new Map<string, FrenchHoliday>();
    for (const h of holidays) map.set(h.date, h);
    return map;
  }, [holidays]);

  const getHolidayForDate = useCallback(
    (date: string | Date | DateTime): FrenchHoliday | undefined => {
      const dateKey = toDateKey(date);
      if (!dateKey) return undefined;
      return holidayByDate.get(dateKey);
    },
    [holidayByDate],
  );

  const isHoliday = useCallback(
    (date: string | Date | DateTime): boolean =>
      getHolidayForDate(date) !== undefined,
    [getHolidayForDate],
  );

  return { holidays, getHolidayForDate, isHoliday };
};

export default useFrenchHolidays;
